import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { ReferralShare } from "@/components/ReferralShare";
import { useLanguage } from "@/context/LanguageContext";

const inputClass =
  "w-full border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:border-slate-900 focus:outline-none bg-white text-slate-900";

export default function RegisterWizard() {
  const { lang, t } = useLanguage();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    university: t.register.universities[0],
    study_program: "",
    semester: "",
    device_types: [],
    device_model: "",
    usage_locations: [],
    declined_categories: [],
    website: "",
  });

  const [touched, setTouched] = useState({});
  const mark = (k) => setTouched((prev) => ({ ...prev, [k]: true }));

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleList = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const stepValid = [
    form.name.trim().length >= 2 && emailValid && form.password.length >= 8 && form.university,
    form.study_program.trim().length > 0 && form.semester.trim().length > 0,
    form.device_types.length > 0 && form.device_model.trim().length > 0,
    form.usage_locations.length > 0,
    true,
  ][step];

  const next = () => {
    if (!stepValid) {
      toast.error(t.register.required);
      return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };

  const submit = async () => {
    setSending(true);
    try {
      const ref = localStorage.getItem("lidlink-ref") || undefined;
      const { device_types, ...rest } = form;
      const payload = { ...rest, device_type: device_types.join(", "), ref, language: lang };
      const res = await api.post("/registrations", payload);
      localStorage.removeItem("lidlink-ref");
      setResult(res.data);
      setDone(true);
    } catch (e) {
      const msg =
        e.response?.status === 409
          ? t.register.errorDuplicate
          : formatApiError(e.response?.data?.detail, t.register.errorGeneric);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white p-6 sm:p-8 border spec-border shadow-sm rounded-sm space-y-6" data-testid="register-success">
        <div className="text-center">
          <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={40} />
          <h3 className="text-2xl font-bold text-slate-950">{t.credit.successTitle}</h3>
          <p className="text-base font-semibold text-gold-700 mt-2">{t.credit.successCredit}</p>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">{t.credit.successSub}</p>
          <p className="text-xs text-slate-500 mt-3 font-mono-spec max-w-md mx-auto">{t.credit.verifyNotice}</p>
        </div>

        <div className="border-t spec-border pt-6">
          <h4 className="text-base font-bold text-slate-950 text-center">{t.credit.inviteTitle}</h4>
          <p className="text-xs font-mono-spec text-slate-500 text-center mt-1 mb-4">{t.credit.yourLink}</p>
          {result?.referral_link && <ReferralShare link={result.referral_link} />}
        </div>

        <div className="text-center">
          <Link
            to="/konto"
            data-testid="register-open-wallet"
            className="inline-block text-sm font-semibold text-slate-950 hover:text-gold-700 transition-colors"
          >
            {t.credit.openWallet}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 sm:p-8 border spec-border shadow-sm rounded-sm" data-testid="register-wizard">
      {/* Step indicator */}
      <div className="flex items-center justify-between mb-6 font-mono-spec text-xs text-slate-500">
        <span data-testid="register-step-indicator">
          {t.register.step} {step + 1} {t.register.of} 5 — {t.register.steps[step]}
        </span>
        <div className="flex gap-1.5">
          {t.register.steps.map((s, i) => (
            <span
              key={s}
              className={`h-1.5 w-6 rounded-sm ${i <= step ? "bg-gold-600" : "bg-slate-200"}`}
            />
          ))}
        </div>
      </div>

      {step === 0 && (
        <div className="space-y-4">
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.name}</label>
              <input
                data-testid="register-name"
                type="text"
                placeholder={t.register.namePh}
                className={`${inputClass} ${touched.name && form.name.trim().length < 2 ? "border-red-500" : ""}`}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                onBlur={() => mark("name")}
              />
              {touched.name && form.name.trim().length < 2 && (
                <p className="text-xs text-red-500 mt-1" data-testid="register-name-error">{t.register.fieldRequired}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.email}</label>
              <input
                data-testid="register-email"
                type="email"
                placeholder={t.register.emailPh}
                className={`${inputClass} ${touched.email && !emailValid ? "border-red-500" : ""}`}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                onBlur={() => mark("email")}
              />
              {touched.email && !emailValid && (
                <p className="text-xs text-red-500 mt-1" data-testid="register-email-error">
                  {form.email.trim() ? t.register.invalidEmail : t.register.fieldRequired}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.password}</label>
            <input
              data-testid="register-password"
              type="password"
              placeholder={t.register.passwordPh}
              className={`${inputClass} ${touched.password && form.password.length < 8 ? "border-red-500" : ""}`}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              onBlur={() => mark("password")}
            />
            {touched.password && form.password.length < 8 ? (
              <p className="text-xs text-red-500 mt-1" data-testid="register-password-error">{t.register.passwordShort}</p>
            ) : (
              <p className="text-[11px] text-slate-500 mt-1 font-mono-spec">{t.register.passwordHint}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.university}</label>
            <select
              data-testid="register-university"
              className={inputClass}
              value={form.university}
              onChange={(e) => set("university", e.target.value)}
            >
              {t.register.universities.map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.studyProgram}</label>
            <input
              data-testid="register-study-program"
              type="text"
              placeholder={t.register.studyProgramPh}
              className={inputClass}
              value={form.study_program}
              onChange={(e) => set("study_program", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.semester}</label>
            <input
              data-testid="register-semester"
              type="text"
              placeholder={t.register.semesterPh}
              className={inputClass}
              value={form.semester}
              onChange={(e) => set("semester", e.target.value)}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.deviceType}</label>
            <p className="text-[11px] text-slate-500 mb-2 font-mono-spec">{t.register.deviceTypeHint}</p>
            <div className="grid grid-cols-2 gap-3">
              {t.register.deviceTypes.map((d) => (
                <button
                  key={d}
                  type="button"
                  data-testid={`register-device-type-${d.includes("Tablet") ? "tablet" : "laptop"}`}
                  onClick={() => toggleList("device_types", d)}
                  className={`p-4 border text-sm font-medium rounded-sm transition-colors ${
                    form.device_types.includes(d)
                      ? "border-gold-600 bg-gold-50 text-gold-800"
                      : "border-slate-300 text-slate-700 hover:border-slate-500"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.deviceModel}</label>
            <input
              data-testid="register-device-model"
              type="text"
              placeholder={t.register.deviceModelPh}
              className={inputClass}
              value={form.device_model}
              onChange={(e) => set("device_model", e.target.value)}
            />
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <label className="block text-xs font-mono-spec text-slate-700 mb-2">{t.register.usage}</label>
          <div className="flex flex-wrap gap-2">
            {t.register.usageOptions.map((u, i) => (
              <button
                key={u}
                type="button"
                data-testid={`register-usage-${i}`}
                onClick={() => toggleList("usage_locations", u)}
                className={`px-3 py-2 border text-xs font-mono-spec rounded-sm transition-colors ${
                  form.usage_locations.includes(u)
                    ? "border-gold-600 bg-gold-50 text-gold-800"
                    : "border-slate-300 text-slate-600 hover:border-slate-500"
                }`}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.register.declined}</label>
          <p className="text-xs text-slate-500 mb-3">{t.register.declinedSub}</p>
          <div className="flex flex-wrap gap-2">
            {t.register.declinedOptions.map((c, i) => (
              <button
                key={c}
                type="button"
                data-testid={`register-decline-${i}`}
                onClick={() => toggleList("declined_categories", c)}
                className={`px-3 py-2 border text-xs font-mono-spec rounded-sm transition-colors ${
                  form.declined_categories.includes(c)
                    ? "border-red-400 bg-red-50 text-red-800 line-through"
                    : "border-slate-300 text-slate-600 hover:border-slate-500"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pt-6 flex items-center justify-between gap-4">
        {step > 0 ? (
          <button
            data-testid="register-back-btn"
            onClick={() => setStep((s) => s - 1)}
            className="text-sm font-medium text-slate-600 hover:text-slate-950"
          >
            ← {t.register.back}
          </button>
        ) : (
          <span className="text-xs text-slate-500 font-mono-spec hidden sm:inline">{t.register.note}</span>
        )}
        <div className="flex flex-col items-end gap-1.5">
          {step < 4 ? (
            <button
              data-testid="register-next-btn"
              onClick={next}
              disabled={!stepValid}
              className="px-7 py-3 bg-slate-950 text-white font-mono-spec text-xs font-bold rounded-full hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t.register.next}
            </button>
          ) : (
            <button
              data-testid="register-submit-btn"
              onClick={submit}
              disabled={sending || !stepValid}
              className="px-7 py-3 bg-slate-950 text-white font-mono-spec text-xs font-bold rounded-full hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t.register.submit}
            </button>
          )}
          {!stepValid && (
            <span className="text-[11px] text-slate-500 font-mono-spec" data-testid="register-step-hint">{t.register.required}</span>
          )}
        </div>
      </div>
    </div>
  );
}
